import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  Modal,
  Form,
  DatePicker,
  InputNumber,
  message,
  Dropdown,
  Menu,
  Checkbox,
  Tooltip,
  Descriptions,
} from 'antd';
import {
  EyeOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  ColumnHeightOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import type { ColumnsType } from 'antd/es/table';
import useDraggableColumns from '../../../components/table/useDraggableColumns';
import type { ReturnOrder } from '../types';
import ReturnOrderProcessing from '../components/ReturnOrderProcessing';
import apiService from '../../../services/apiService';
import { PersistentStorage } from '../../../utils/persistentStorage';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

// 註冊 Pie 圖所需的 Chart.js 元件（僅此文件）
ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

// ===== TEMP: 測試資料開關（審閱後請移除） =====
// 設為 true 以在本頁展示內建的 mock 測試資料
const USE_TEMP_MOCK_DATA = false; // TODO: 審閱後刪除

const ReturnManagement: React.FC = () => {
    const { t } = useTranslation();
  const [returnOrders, setReturnOrders] = useState<ReturnOrder[]>([]);
  const [filteredReturns, setFilteredReturns] = useState<ReturnOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[any, any] | null>(null);
  const [processingModalVisible, setProcessingModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<ReturnOrder | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [refundChartVisible, setRefundChartVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [previewReturnNumber, setPreviewReturnNumber] = useState<string>('');

  // 列設置下拉菜單可見狀態
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);

  // 列設置和排序狀態
  const [columnSettings, setColumnSettings] = useState(() => {
    const saved = PersistentStorage.get('returnManagement_columnSettings', null);
    return saved || {
      returnNumber: { visible: true, order: 0 },
      originalOrderNumber: { visible: true, order: 1 },
      customerName: { visible: true, order: 2 },
      items: { visible: true, order: 3 },
      totalRefundAmount: { visible: true, order: 4 },
      status: { visible: true, order: 5 },
      createdAt: { visible: true, order: 6 },
      actions: { visible: true, order: 7 }
    };
  });
  
  const [sortConfig, setSortConfig] = useState<{field: string, direction: 'asc' | 'desc'} | null>(() => {
    const saved = PersistentStorage.get('returnManagement_sortConfig', null);
    return saved || null;
  });

  // 展開收起控制
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  // 模拟退貨申請數據
  const mockReturnOrders: ReturnOrder[] = [
    {
      id: '1',
      returnNumber: 'RET-20250115-0001',
      originalOrderId: '3',
      originalOrder: {
        id: '3',
        orderNumber: 'SO-2024-003',
        customerId: '3',
        customer: {
          id: '3',
          name: '客戶C',
          email: 'customer.c@example.com',
          phone: '+86-138-0000-0003',
          address: '深圳市南山區科技園',
          city: 'Shenzhen',
          country: 'China',
          company: '深圳科技有限公司',
          contactPerson: '李先生',
          paymentTerms: 'Net 30'
        },
        orderDate: new Date('2024-01-17'),
        deliveryDate: new Date('2024-01-24'),
        status: 'shipped',
        items: [
          {
            id: '3',
            productId: 'bt-speaker-002',
            product: {
              id: 'bt-speaker-002',
              sku: 'BS-002',
              name: 'Bluetooth Speaker',
              description: t('高品質藍牙音箱'),
              unitPrice: 399.95,
              category: '電子產品',
              stockQuantity: 50,
              warehouse: 'Main Warehouse',
              uom: 'PCS',
              barcode: '1234567890125'
            },
            quantity: 5,
            unitPrice: 399.95,
            discount: 0,
            discountType: 'percentage' as const,
            taxRate: 10,
            lineTotal: 1999.75,
            deliveredQuantity: 5,
            returnedQuantity: 0
          }
        ],
        subtotal: 2089.74,
        taxAmount: 208.97,
        discountAmount: 0,
        shippingAmount: 30,
        totalAmount: 2328.71,
        currency: 'CNY',
        warehouseId: 'main-warehouse',
        warehouse: {
          id: 'main-warehouse',
          name: 'Main Warehouse',
          code: 'MAIN',
          address: '789 Storage Blvd',
          manager: 'Mike Wilson',
          isActive: true
        },
        paymentTerms: 'Net 30',
        priority: 'normal',
        source: 'online',
        createdBy: 'admin',
        createdAt: new Date('2024-01-17'),
        updatedAt: new Date('2024-01-17'),
        deliveryStatus: 'partial',
        invoiceStatus: 'none'
      },
      customerId: '3',
      customer: {
        id: '3',
        name: '客戶C',
        email: 'customer.c@example.com',
        phone: '+86-138-0000-0003',
        address: '深圳市南山區科技園',
        city: 'Shenzhen',
        country: 'China',
        company: '深圳科技有限公司',
        contactPerson: '李先生',
        paymentTerms: 'Net 30'
      },
      returnDate: new Date('2025-01-15'),
      status: 'pending',
      items: [
        {
          id: '1',
          originalOrderItemId: '3',
          productId: 'bt-speaker-002',
          product: {
            id: 'bt-speaker-002',
            sku: 'BS-002',
            name: 'Bluetooth Speaker',
            description: t('高品質藍牙音箱'),
            unitPrice: 399.95,
            category: '電子產品',
            stockQuantity: 50,
            warehouse: 'Main Warehouse',
            uom: 'PCS',
            barcode: '1234567890125'
          },
          originalQuantity: 5,
          returnQuantity: 1,
          unitPrice: 399.95,
          returnReason: '其中一個蓝牙音箱在运輸過程中损坏',
          condition: 'damaged',
          restockable: false,
          notes: '损坏的蓝牙音箱需要退回處理'
        }
      ],
      returnReason: '其中一個蓝牙音箱在运輸過程中损坏，客戶要求退貨處理。',
      totalRefundAmount: 399.95,
      refundMethod: 'original_payment',
      handlingMethod: 'refund',
      warehouseId: 'main-warehouse',
      warehouse: {
        id: 'main-warehouse',
        name: 'Main Warehouse',
        code: 'MAIN',
        address: '789 Storage Blvd',
        manager: 'Mike Wilson',
        isActive: true
      },
      createdBy: 'current-user',
      createdAt: new Date('2025-01-15'),
      updatedAt: new Date('2025-01-15'),
    },
    {
      id: '2',
      returnNumber: 'RET-20250116-0002',
      originalOrderId: '4',
      originalOrder: {
        id: '4',
        orderNumber: 'SO-2024-004',
        customerId: '4',
        customer: {
          id: '4',
          name: '上海貿易公司',
          email: 'info@shanghai-trade.com',
          phone: '+86-021-1234-5678',
          address: '上海市浦東新區陸家嘴金融街',
          city: 'Shanghai',
          country: 'China',
          company: '上海國際貿易有限公司',
          contactPerson: '王經理',
          paymentTerms: 'Net 15'
        },
        paymentTerms: 'Net 15',
        priority: 'high',
        source: 'phone',
        createdBy: 'sales01',
        createdAt: new Date('2024-01-20'),
        updatedAt: new Date('2024-01-20'),
      },
      customerId: '4',
      customer: {
        id: '4',
        name: '上海貿易公司',
        email: 'info@shanghai-trade.com',
        phone: '+86-021-1234-5678',
        address: '上海市浦東新區陸家嘴金融街',
        city: 'Shanghai',
        country: 'China',
        company: '上海國際貿易有限公司',
        contactPerson: '王經理',
        paymentTerms: 'Net 15'
      },
      reason: 'product_damage',
      description: t('收到商品時發現包裝損壞，內部產品可能受損'),
      items: [],
      status: 'pending',
      priority: 'high',
      returnType: 'refund',
      refundMethod: 'original_payment',
      totalRefundAmount: 8999.00,
      inspectionRequired: true,
      inspectionStatus: 'pending',
      attachments: ['damage_report.pdf'],
      internalNotes: '高價值商品，需要仔細檢查',
      customerNotes: '希望能快速處理',
      estimatedProcessingTime: 5,
      actualProcessingTime: 0,
      createdBy: 'system',
      assignedTo: 'quality_dept',
      departmentNotes: '',
      handlingNotes: '',
      createdAt: new Date('2025-01-16'),
      updatedAt: new Date('2025-01-16')
    },
    {
      id: '3',
      returnNumber: 'RET-20250114-0003',
      originalOrderId: '5',
      originalOrder: {
        id: '5',
        orderNumber: 'SO-2024-005',
        customerId: '5',
        customer: {
          id: '5',
          name: '廣州零售商店',
          email: 'orders@gz-retail.com',
          phone: '+86-020-8888-9999',
          address: '廣州市天河區珠江新城',
          city: 'Guangzhou',
          country: 'China',
          company: '廣州連鎖零售有限公司',
          contactPerson: '陳店長',
          paymentTerms: 'Net 30'
        },
        paymentTerms: 'Net 30',
        priority: 'normal',
        source: 'phone',
        createdBy: 'sales02',
        createdAt: new Date('2024-01-10'),
        updatedAt: new Date('2024-01-10'),
      },
      customerId: '5',
      customer: {
        id: '5',
        name: '廣州零售商店',
        email: 'orders@gz-retail.com',
        phone: '+86-020-8888-9999',
        address: '廣州市天河區珠江新城',
        city: 'Guangzhou',
        country: 'China',
        company: '廣州連鎖零售有限公司',
        contactPerson: '陳店長',
        paymentTerms: 'Net 30'
      },
      reason: 'size_mismatch',
      description: t('客戶反饋商品尺寸不合適'),
      items: [],
      status: 'completed',
      priority: 'normal',
      returnType: 'exchange',
      refundMethod: 'store_credit',
      totalRefundAmount: 1348.50,
      inspectionRequired: false,
      inspectionStatus: 'passed',
      attachments: [],
      internalNotes: '商品狀況良好，已重新入庫',
      customerNotes: '希望換成正確尺寸',
      estimatedProcessingTime: 2,
      actualProcessingTime: 1,
      createdBy: 'customer_service',
      assignedTo: 'warehouse_team',
      departmentNotes: '已完成質檢',
      handlingNotes: '已處理完成，商品重新入庫',
      createdAt: new Date('2025-01-14'),
      updatedAt: new Date('2025-01-15')
    },
    {
      id: '4',
      returnNumber: 'RET-20250113-0004',
      originalOrderId: '6',
      originalOrder: {
        id: '6',
        orderNumber: 'SO-2024-006',
        customerId: '6',
        customer: {
          id: '6',
          name: '北京辦公用品公司',
          email: 'procurement@bj-office.com',
          phone: '+86-010-5678-1234',
          address: '北京市朝陽區建國門外大街',
          city: 'Beijing',
          country: 'China',
          company: '北京辦公設備採購有限公司',
          contactPerson: '張採購',
          paymentTerms: 'Net 45'
        },
        paymentTerms: 'Net 45',
        priority: 'normal',
        source: 'online',
        createdBy: 'b2b_system',
        createdAt: new Date('2024-01-05'),
        updatedAt: new Date('2024-01-05'),
      },
      customerId: '6',
      customer: {
        id: '6',
        name: '北京辦公用品公司',
        email: 'procurement@bj-office.com',
        phone: '+86-010-5678-1234',
        address: '北京市朝陽區建國門外大街',
        city: 'Beijing',
        country: 'China',
        company: '北京辦公設備採購有限公司',
        contactPerson: '張採購',
        paymentTerms: 'Net 45'
      },
      reason: 'quality_issue',
      description: t('辦公椅存在質量問題，無法正常使用'),
      items: [],
      status: 'processing',
      priority: 'high',
      returnType: 'replacement',
      refundMethod: 'other',
      totalRefundAmount: 3897.00,
      inspectionRequired: true,
      inspectionStatus: 'in_progress',
      attachments: ['quality_report.pdf'],
      internalNotes: '聯繫供應商進行質量調查',
      customerNotes: '影響正常工作，希望盡快處理',
      estimatedProcessingTime: 7,
      actualProcessingTime: 3,
      createdBy: 'quality_dept',
      assignedTo: 'supplier_relations',
      departmentNotes: '已確認質量問題',
      handlingNotes: '正在與供應商協調更換',
      createdAt: new Date('2025-01-13'),
      updatedAt: new Date('2025-01-16')
    },
    {
      id: '5',
      returnNumber: 'RET-20250112-0005',
      originalOrderId: '7',
      originalOrder: {
        id: '7',
        orderNumber: 'SO-2024-007',
        customerId: '7',
        customer: {
          id: '7',
          name: '個人客戶-李女士',
          email: 'li.customer@email.com',
          phone: '+86-138-0000-7777',
          address: '成都市錦江區春熙路',
          city: 'Chengdu',
          country: 'China',
          company: '',
          contactPerson: '李女士',
          paymentTerms: 'Immediate'
        },
        paymentTerms: 'Immediate',
        priority: 'normal',
        source: 'online',
        createdBy: 'web_system',
        createdAt: new Date('2024-01-08'),
        updatedAt: new Date('2024-01-08'),
      },
      customerId: '7',
      customer: {
        id: '7',
        name: '個人客戶-李女士',
        email: 'li.customer@email.com',
        phone: '+86-138-0000-7777',
        address: '成都市錦江區春熙路',
        city: 'Chengdu',
        country: 'China',
        company: '',
        contactPerson: '李女士',
        paymentTerms: 'Immediate'
      },
      reason: 'change_of_mind',
      description: t('購買後發現不需要，希望退貨'),
      items: [],
      status: 'approved',
      priority: 'normal',
      returnType: 'refund',
      refundMethod: 'original_payment',
      totalRefundAmount: 2199.00,
      inspectionRequired: true,
      inspectionStatus: 'passed',
      attachments: [],
      internalNotes: '7天無理由退貨，符合政策',
      customerNotes: '商品很好，只是不需要了',
      estimatedProcessingTime: 3,
      actualProcessingTime: 2,
      createdBy: 'customer_service',
      assignedTo: 'returns_team',
      departmentNotes: '商品狀況良好',
      handlingNotes: '已批准退貨，等待寄回',
      createdAt: new Date('2025-01-12'),
      updatedAt: new Date('2025-01-14')
    },
    {
      id: '6',
      returnNumber: 'RET-20250111-0006',
      originalOrderId: '8',
      originalOrder: {
        id: '8',
        orderNumber: 'SO-2024-008',
        customerId: '8',
        customer: {
          id: '8',
          name: '杭州電子商務公司',
          email: 'support@hz-ecommerce.com',
          phone: '+86-0571-8888-6666',
          address: '杭州市西湖區文三路',
          city: 'Hangzhou',
          country: 'China',
          company: '杭州電子商務科技有限公司',
          contactPerson: '馬總監',
          paymentTerms: 'Net 20'
        },
        paymentTerms: 'Net 20',
        priority: 'high',
        source: 'online',
        createdBy: 'api_system',
        createdAt: new Date('2024-01-03'),
        updatedAt: new Date('2024-01-03'),
      },
      customerId: '8',
      customer: {
        id: '8',
        name: '杭州電子商務公司',
        email: 'support@hz-ecommerce.com',
        phone: '+86-0571-8888-6666',
        address: '杭州市西湖區文三路',
        city: 'Hangzhou',
        country: 'China',
        company: '杭州電子商務科技有限公司',
        contactPerson: '馬總監',
        paymentTerms: 'Net 20'
      },
      reason: 'wrong_product',
      description: t('發貨錯誤，收到錯誤的產品型號'),
      items: [],
      status: 'rejected',
      priority: 'urgent',
      returnType: 'exchange',
      refundMethod: 'other',
      totalRefundAmount: 0,
      inspectionRequired: false,
      inspectionStatus: 'not_required',
      attachments: ['order_comparison.pdf'],
      internalNotes: '確認為我方錯誤，緊急處理',
      customerNotes: '希望盡快換成正確產品',
      estimatedProcessingTime: 1,
      actualProcessingTime: 0,
      createdBy: 'logistics_dept',
      assignedTo: 'operations_manager',
      departmentNotes: '我方責任，已安排換貨',
      handlingNotes: '直接安排換貨，無需退回',
      createdAt: new Date('2025-01-11'),
      updatedAt: new Date('2025-01-11')
    },
    {
      id: '7',
      returnNumber: 'RET-20250110-0007',
      originalOrderId: '9',
      originalOrder: {
        id: '9',
        orderNumber: 'SO-2024-009',
        customerId: '9',
        customer: {
          id: '9',
          name: '深圳科技園公司',
          email: 'tech@shenzhen-park.com',
          phone: '+86-0755-2233-4455',
          address: '深圳市南山區科技園南區',
          city: 'Shenzhen',
          country: 'China',
          company: '深圳科技園發展有限公司',
          contactPerson: '陳工程師',
          paymentTerms: 'Net 30'
        },
        paymentTerms: 'Net 30',
        priority: 'normal',
        source: 'manual',
        createdBy: 'sales03',
        createdAt: new Date('2024-01-02'),
        updatedAt: new Date('2024-01-02'),
      },
      customerId: '9',
      customer: {
        id: '9',
        name: '深圳科技園公司',
        email: 'tech@shenzhen-park.com',
        phone: '+86-0755-2233-4455',
        address: '深圳市南山區科技園南區',
        city: 'Shenzhen',
        country: 'China',
        company: '深圳科技園發展有限公司',
        contactPerson: '陳工程師',
        paymentTerms: 'Net 30'
      },
      reason: 'late_delivery',
      description: t('送貨延遲超過約定時間，影響項目進度'),
      items: [],
      status: 'pending',
      priority: 'normal',
      returnType: 'partial_refund',
      refundMethod: 'other',
      totalRefundAmount: 500.00,
      inspectionRequired: false,
      inspectionStatus: 'not_required',
      attachments: ['delivery_delay_evidence.pdf'],
      internalNotes: '物流延遲導致，需要給予補償',
      customerNotes: '希望給予延遲補償',
      estimatedProcessingTime: 3,
      actualProcessingTime: 0,
      createdBy: 'logistics_dept',
      assignedTo: 'customer_relations',
      departmentNotes: '確認物流延遲責任',
      handlingNotes: '',
      createdAt: new Date('2025-01-10'),
      updatedAt: new Date('2025-01-10')
    },
    {
      id: '8',
      returnNumber: 'RET-20250109-0008',
      originalOrderId: '10',
      originalOrder: {
        id: '10',
        orderNumber: 'SO-2024-010',
        customerId: '10',
        customer: {
          id: '10',
          name: '西安教育機構',
          email: 'procurement@xian-edu.com',
          phone: '+86-029-8765-4321',
          address: '西安市雁塔區高新技術開發區',
          city: 'Xian',
          country: 'China',
          company: '西安現代教育科技有限公司',
          contactPerson: '王老師',
          paymentTerms: 'Net 60'
        },
        paymentTerms: 'Net 60',
        priority: 'low',
        source: 'online',
        createdBy: 'tender_system',
        createdAt: new Date('2023-12-28'),
        updatedAt: new Date('2023-12-28'),
      },
      customerId: '10',
      customer: {
        id: '10',
        name: '西安教育機構',
        email: 'procurement@xian-edu.com',
        phone: '+86-029-8765-4321',
        address: '西安市雁塔區高新技術開發區',
        city: 'Xian',
        country: 'China',
        company: '西安現代教育科技有限公司',
        contactPerson: '王老師',
        paymentTerms: 'Net 60'
      },
      reason: 'budget_change',
      description: t('學校預算調整，需要退回部分設備'),
      items: [],
      status: 'approved',
      priority: 'low',
      returnType: 'refund',
      refundMethod: 'other',
      totalRefundAmount: 15600.00,
      inspectionRequired: true,
      inspectionStatus: 'passed',
      attachments: ['budget_approval.pdf'],
      internalNotes: '教育機構特殊情況，可以接受退貨',
      customerNotes: '預算調整，感謝理解',
      estimatedProcessingTime: 10,
      actualProcessingTime: 8,
      createdBy: 'sales_manager',
      assignedTo: 'finance_dept',
      departmentNotes: '特殊客戶，批准退貨',
      handlingNotes: '已批准，正在處理退款',
      createdAt: new Date('2025-01-09'),
      updatedAt: new Date('2025-01-17')
    }
  ];

  useEffect(() => {
    const formatDateInput = (val: any) => {
      if (!val) return undefined;
      try { if (typeof val?.format === 'function') return val.format('YYYY-MM-DD'); } catch {}
      try { return new Date(val).toISOString().split('T')[0]; } catch { return undefined; }
    };
    const df = formatDateInput(dateRange?.[0]);
    const dt = formatDateInput(dateRange?.[1]);
    loadReturnOrders(currentPage, pageSize, searchText, statusFilter, df, dt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  useEffect(() => {
    // 查詢條件變更時回到第一頁並拉取後端
    setCurrentPage(1);
    const formatDateInput = (val: any) => {
      if (!val) return undefined;
      try { if (typeof val?.format === 'function') return val.format('YYYY-MM-DD'); } catch {}
      try { return new Date(val).toISOString().split('T')[0]; } catch { return undefined; }
    };
    const df = formatDateInput(dateRange?.[0]);
    const dt = formatDateInput(dateRange?.[1]);
    loadReturnOrders(1, pageSize, searchText, statusFilter, df, dt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, statusFilter, dateRange]);

  const loadReturnOrders = async (page = 1, limit = 20, search?: string, status?: string, dateFrom?: string, dateTo?: string) => {
    try {
      setLoading(true);
      // TEMP: 使用本地測試資料（審閱後請移除）
      if (USE_TEMP_MOCK_DATA) {
        const mapped = mockReturnOrders;
        setReturnOrders(mapped);
        const filteredByDate = (() => {
          if (!dateFrom && !dateTo) return mapped;
          const startTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : undefined;
          const endTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : undefined;
          return mapped.filter(it => {
            const ct = it.createdAt ? new Date(it.createdAt as any).getTime() : 0;
            if (startTs !== undefined && ct < startTs) return false;
            if (endTs !== undefined && ct > endTs) return false;
            return true;
          });
        })();
        setFilteredReturns(filteredByDate);
        setTotalCount(mapped.length);
        return;
      }

      const resp: any = await (apiService as any).getReturns({ page, limit, search, status, dateFrom, dateTo });
      // 後端返回 { data: rows, pagination: { total, page, limit } }
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      const mapped: ReturnOrder[] = rows.map((r: any) => ({
        id: r.id,
        returnNumber: r.returnNumber,
        originalOrderId: r.originalOrderId,
        originalOrder: { orderNumber: r.originalOrderId } as any,
        customerId: r.customerId,
        customer: { name: r.customerId } as any,
        returnDate: r.returnDate,
        status: r.status,
        items: [],
        returnReason: r.returnReason,
        totalRefundAmount: Number(r.totalRefundAmount || 0),
        refundMethod: r.refundMethod,
        handlingMethod: r.handlingMethod,
        warehouseId: r.warehouseId,
        createdBy: r.createdBy,
        processedBy: r.processedBy,
        processedAt: r.processedAt,
        merchantConfirmation: r.merchantConfirmation,
        internalNotes: r.internalNotes,
        customerNotes: r.customerNotes,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      }));
      setReturnOrders(mapped);
      // 後端若未支持，前端做日期範圍過濾後備
      const filteredByDate = (() => {
        if (!dateFrom && !dateTo) return mapped;
        const startTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : undefined;
        const endTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : undefined;
        return mapped.filter(it => {
          const ct = it.createdAt ? new Date(it.createdAt as any).getTime() : 0;
          if (startTs !== undefined && ct < startTs) return false;
          if (endTs !== undefined && ct > endTs) return false;
          return true;
        });
      })();
      setFilteredReturns(filteredByDate);
      const total = Number(resp?.pagination?.total || mapped.length);
      setTotalCount(total);
    } catch (error) {
      console.error('加載退貨列表失敗:', error);
      message.error(t('加載退貨列表失敗'));
    } finally {
      setLoading(false);
    }
  };

  // 導出功能 - 客戶端CSV生成
  const handleExportExcel = () => {
    try {
      // 獲取過濾後的退貨訂單數據
      const filteredData = filteredReturns;
      
      // 生成CSV內容
      const csvContent = generateReturnOrdersCSVContent(filteredData);
      
      // 創建Blob並下載
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `return_orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success(t('退貨管理數據已成功導出'));
    } catch (error) {
      console.error('導出失敗:', error);
      message.error(t('導出失敗，請稍後重試'));
    }
  };

  // 生成退貨訂單CSV內容
  const generateReturnOrdersCSVContent = (data: ReturnOrder[]) => {
    const headers = [
      '退貨編號', '原訂單ID', '原訂單號', '客戶ID', '客戶名稱', '退貨日期', '狀態',
      '退貨原因', '總退款金額', '退款方式', '處理方式', '倉庫ID', '倉庫名稱',
      '創建人', '處理人', '處理時間', '商家確認', '處理備註', '創建時間', '更新時間'
    ];

    const rows = data.map(item => [
      item.returnNumber || '',
      item.originalOrderId || '',
      item.originalOrder?.orderNumber || '',
      item.customerId || '',
      item.customer?.name || '',
      item.returnDate ? `\u200B${new Date(item.returnDate).toISOString().split('T')[0]}` : '',
      item.status || '',
      item.returnReason || '',
      item.totalRefundAmount || '',
      item.refundMethod || '',
      item.handlingMethod || '',
      item.warehouseId || '',
      item.warehouse?.name || '',
      item.createdBy || '',
      item.processedBy || '',
      item.processedAt ? new Date(item.processedAt).toISOString().split('T')[0] : '',
      item.merchantConfirmation || '',
      item.handlingNotes || '',
      item.createdAt ? `\u200B${new Date(item.createdAt).toISOString().split('T')[0]}` : '',
      item.updatedAt ? `\u200B${new Date(item.updatedAt).toISOString().split('T')[0]}` : ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const handleProcessReturn = async (record: ReturnOrder) => {
    try {
      setLoading(true);
      // 獲取詳情（含 items），並構造最小化的 SalesOrder 供處理組件使用
      const detail: any = await apiService.getReturnById(record.id);
      const items = Array.isArray(detail?.items) ? detail.items : [];
      const syntheticOrder: any = {
        id: detail.originalOrderId || record.originalOrderId,
        orderNumber: detail.originalOrderId || record.originalOrderId,
        customer: { name: detail.customerId || record.customerId || '-' },
        totalAmount: Number(detail.totalRefundAmount || record.totalRefundAmount || 0),
        items: items.map((it: any, idx: number) => ({
          id: it.originalOrderItemId || String(idx),
          productId: it.productId || undefined,
          product: { name: it.productName || '-', sku: it.productSku || '-' },
          quantity: Number(it.originalQuantity || 0),
          deliveredQuantity: Number(it.originalQuantity || 0),
          returnedQuantity: Number(it.returnQuantity || 0),
          unitPrice: Number(it.unitPrice || 0),
        })),
      };
      setSelectedReturn({ ...record, items, originalOrder: syntheticOrder } as any);
      setProcessingModalVisible(true);
    } catch (e) {
      console.error('加載退貨詳情失敗:', e);
      message.error(t('加載退貨詳情失敗'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = (returnId: string, status: string, notes?: string) => {
    const updatedReturns = returnOrders.map(returnOrder => {
      if (returnOrder.id === returnId) {
        return {
          ...returnOrder,
          status: status as any,
          updatedAt: new Date(),
          handlingNotes: notes
        };
      }
      return returnOrder;
    });
    setReturnOrders(updatedReturns);
  };

  const handleProcessingComplete = () => {
    setProcessingModalVisible(false);
    setSelectedReturn(null);
    loadReturnOrders(currentPage, pageSize, searchText, statusFilter);
  };

  // 查看詳情：拉取後端詳情（含 items）供只讀視圖使用
  const handleViewDetails = async (record: ReturnOrder) => {
    try {
      setLoading(true);
      const detail: any = await apiService.getReturnById(record.id);
      const items = Array.isArray(detail?.items) ? detail.items : [];
      const syntheticOrder: any = {
        id: detail.originalOrderId || record.originalOrderId,
        orderNumber: detail.originalOrderId || record.originalOrderId,
        customer: { name: detail.customerId || record.customerId || '-' },
        totalAmount: Number(detail.totalRefundAmount || record.totalRefundAmount || 0),
        items: (items || []).map((it: any, idx: number) => ({
          id: it.originalOrderItemId || String(idx),
          productId: it.productId || undefined,
          product: { name: it.productName || '-', sku: it.productSku || '-' },
          quantity: Number(it.originalQuantity || 0),
          deliveredQuantity: Number(it.originalQuantity || 0),
          returnedQuantity: Number(it.returnQuantity || 0),
          unitPrice: Number(it.unitPrice || 0),
        })),
      };
      setSelectedReturn({ ...record, items, originalOrder: syntheticOrder } as any);
      setDetailsVisible(true);
    } catch (e) {
      console.error('加載退貨詳情失敗:', e);
      message.error(t('加載退貨詳情失敗'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'orange',
      approved: 'blue',
      processing: 'purple',
      completed: 'green',
      rejected: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts = {
      pending: t('待處理'),
      approved: t('已批準'),
      processing: t('處理中'),
      completed: t('已完成'),
      rejected: t('已拒絕')
    };
    return texts[status as keyof typeof texts] || status;
  };

  const calculateStatistics = () => {
    const total = returnOrders.length;
    const pending = returnOrders.filter(r => r.status === 'pending').length;
    const processing = returnOrders.filter(r => r.status === 'processing').length;
    const completed = returnOrders.filter(r => r.status === 'completed').length;
    const totalRefundAmount = returnOrders.reduce((sum, r) => sum + r.totalRefundAmount, 0);

    return { total, pending, processing, completed, totalRefundAmount };
  };

  const statistics = calculateStatistics();

  // 保存列設置到本地存儲
  const saveColumnSettings = (newSettings: any) => {
    setColumnSettings(newSettings);
    PersistentStorage.set('returnManagement_columnSettings', newSettings);
  };

  // 保存排序設置到本地存儲
  const saveSortConfig = (newSortConfig: any) => {
    setSortConfig(newSortConfig);
    PersistentStorage.set('returnManagement_sortConfig', newSortConfig);
  };

  // 切換列可見性
  const toggleColumnVisibility = (columnKey: string, visible: boolean) => {
    const newSettings: any = {
      ...columnSettings,
      [columnKey]: { ...(columnSettings as any)[columnKey], visible }
    } as any;
    saveColumnSettings(newSettings);
  };

  // 排序數據
  const sortData = (data: ReturnOrder[]) => {
    if (!sortConfig) return data;
    
    return [...data].sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortConfig.field) {
        case 'returnNumber':
          aValue = a.returnNumber;
          bValue = b.returnNumber;
          break;
        case 'originalOrderNumber':
          aValue = a.originalOrder?.orderNumber || '';
          bValue = b.originalOrder?.orderNumber || '';
          break;
        case 'customerName':
          aValue = a.customer?.name || '';
          bValue = b.customer?.name || '';
          break;
        case 'totalRefundAmount':
          aValue = a.totalRefundAmount;
          bValue = b.totalRefundAmount;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // 處理列標題點擊排序
  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.field === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    saveSortConfig({ field, direction });
  };

  // 獲取排序圖標
  const getSortIcon = (field: string) => {
    if (sortConfig?.field !== field) return null;
    return sortConfig.direction === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />;
  };

  // 創建帶有排序功能的列定義
  const createColumns = (): ColumnsType<ReturnOrder> => {
    const baseColumns = [
      {
        title: (
          <div 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleSort('returnNumber')}
          >
            {t('退貨申請號')} {getSortIcon('returnNumber')}
          </div>
        ),
        dataIndex: 'returnNumber',
        key: 'returnNumber',
        render: (text: string) => <Text strong>{text}</Text>,
      },
      {
        title: (
          <div 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleSort('originalOrderNumber')}
          >
            {t('原訂單號')} {getSortIcon('originalOrderNumber')}
          </div>
        ),
        dataIndex: ['originalOrder', 'orderNumber'],
        key: 'originalOrderNumber',
        render: (text: string) => <Text code>{text}</Text>,
      },
      {
        title: (
          <div 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleSort('customerName')}
          >
            {t('客戶')} {getSortIcon('customerName')}
          </div>
        ),
        dataIndex: ['customer', 'name'],
        key: 'customerName',
      },
      {
        title: t('退貨商品'),
        dataIndex: 'items',
        key: 'items',
        render: (items: any[]) => (
          <div>
            {items.map((item, index) => (
              <div key={index} style={{ fontSize: '12px' }}>
                {item.product.name} × {item.returnQuantity}
              </div>
            ))}
          </div>
        ),
      },
      {
        title: (
          <div 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleSort('totalRefundAmount')}
          >
            {t('退款金額')} {getSortIcon('totalRefundAmount')}
          </div>
        ),
        dataIndex: 'totalRefundAmount',
        key: 'totalRefundAmount',
        render: (amount: number) => (
          <Text strong style={{ color: '#f5222d' }}>
            ¥{amount.toFixed(2)}
          </Text>
        ),
      },
      {
        title: (
          <div 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleSort('status')}
          >
            {t('狀態')} {getSortIcon('status')}
          </div>
        ),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={getStatusColor(status)}>
            {getStatusText(status)}
          </Tag>
        ),
      },
      {
        title: (
          <div 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleSort('createdAt')}
          >
            {t('申請日期')} {getSortIcon('createdAt')}
          </div>
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: Date) => new Date(date).toLocaleDateString(),
      },
      {
        title: t('操作'),
        key: 'actions',
        width: 200,
        render: (_: any, record: ReturnOrder) => (
          <Space size="small">
            <Tooltip>
              <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetails(record)}
              >
                {t('詳情')}
                                      </Button>
            </Tooltip>
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => handleProcessReturn(record)}
              disabled={record.status === 'completed' || record.status === 'rejected'}
            >
              {t('處理')}
                                </Button>
          </Space>
        ),
      },
    ];

    // 根據列設置過濾和排序列
    return baseColumns
      .filter(col => (columnSettings as any)[col.key as string]?.visible !== false)
      .sort((a, b) => {
        const orderA = (columnSettings as any)[a.key as string]?.order || 0;
        const orderB = (columnSettings as any)[b.key as string]?.order || 0;
        return orderA - orderB;
      });
  };

  const columns = createColumns();

  // 退貨管理主表支持拖拽列排序（禁用 actions）
  const { columns: draggableReturnColumns, order: returnOrder } = useDraggableColumns<any>({
    columns: columns as any,
    disableDragKeys: ['actions'],
    storageKey: 'return-management-table',
  });

  useEffect(() => {
    if (!returnOrder || returnOrder.length === 0) return;
    // 同步列順序到 columnSettings（若有需要）
    // 此頁以 PersistentStorage 自定義格式存儲，僅在鍵存在時同步
    setColumnSettings((prev: any) => {
      try {
        const next: any = { ...prev };
        returnOrder.forEach((k: string, idx: number) => {
          if (next[k]) next[k] = { ...next[k], order: idx };
        });
        PersistentStorage.set('returnManagement_columnSettings', next);
        return next;
      } catch {
        return prev;
      }
    });
  }, [returnOrder]);

  // 列設置下拉菜單
  const columnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(columnSettings).map(([key, config]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              toggleColumnVisibility(key, !(config.visible ?? true));
            }}
          >
            <Checkbox
              checked={config.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                toggleColumnVisibility(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {getColumnDisplayName(key)}
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
  );

  // 獲取列顯示名稱
  function getColumnDisplayName(key: string): string {
    const names: Record<string, string> = {
      returnNumber: t('退貨申請號'),
      originalOrderNumber: t('原訂單號'),
      customerName: t('客戶'),
      items: t('退貨商品'),
      totalRefundAmount: t('退款金額'),
      status: t('狀態'),
      createdAt: t('申請日期'),
      actions: t('操作')
    };
    return names[key] || key;
  }

  return (
    <div style={{ padding: '24px' }}>

      {/* 標題和展開收起按鈕 */}
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>{t('退貨管理')}</Title>
          <Text type="secondary">{t('管理客戶退貨申請和處理流程')}</Text>
        </div>
        <Button
          type="text"
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={toggleCollapsed}
          style={{ fontSize: 16 }}
        />
      </div>

      {/* KPI 儀表板 */}
      {!collapsed && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card hoverable onClick={() => { setSearchText(''); setStatusFilter(''); setDateRange(null); }} style={{ cursor: 'pointer' }}>
              <Statistic
                title={t('總退貨申請')}
                value={statistics.total}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable onClick={() => { setSearchText(''); setDateRange(null); setStatusFilter('pending'); }} style={{ cursor: 'pointer' }}>
              <Statistic
                title={t('待處理')}
                value={statistics.pending}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable onClick={() => { setSearchText(''); setDateRange(null); setStatusFilter('processing'); }} style={{ cursor: 'pointer' }}>
              <Statistic
                title={t('處理中')}
                value={statistics.processing}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable onClick={() => setRefundChartVisible(true)} style={{ cursor: 'pointer' }}>
              <Statistic
                title={t('總退款金額')}
                value={`¥${statistics.totalRefundAmount.toFixed(2)}`}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 搜索和過濾 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} sm={12} md={8} lg={8}>
            <Search
              placeholder={t('搜索退貨申請號、訂單號或客戶...')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => {
                const formatDateInput = (val: any) => {
                  if (!val) return undefined;
                  try { if (typeof val?.format === 'function') return val.format('YYYY-MM-DD'); } catch {}
                  try { return new Date(val).toISOString().split('T')[0]; } catch { return undefined; }
                };
                const df = formatDateInput(dateRange?.[0]);
                const dt = formatDateInput(dateRange?.[1]);
                loadReturnOrders(1, pageSize, searchText, statusFilter, df, dt);
              }}
              allowClear
              size="small"
            />
          </Col>
          <Col xs={24} sm={12} md={5} lg={5}>
            <Select
              placeholder={t('狀態')}
              value={statusFilter || undefined}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear
              size="small"
            >
              <Option value="pending">{t('待處理')}</Option>
              <Option value="approved">{t('已批準')}</Option>
              <Option value="processing">{t('處理中')}</Option>
              <Option value="completed">{t('已完成')}</Option>
              <Option value="rejected">{t('已拒絕')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7} lg={7}>
            <RangePicker
              value={dateRange as any}
              onChange={(v) => setDateRange(v as any)}
              allowClear
              size="small"
            />
          </Col>
          <Col xs={24} sm={24} md={4} lg={4}>
            <Row justify="end">
              <Space wrap size={8}>
                <Button
                  onClick={() => {
                    setSearchText('');
                    setStatusFilter('');
                    setDateRange(null);
                    loadReturnOrders(1, pageSize, '', '', undefined, undefined);
                  }}
                  size="small"
                >
                  {t('重置')}
                                                  </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    const formatDateInput = (val: any) => {
                      if (!val) return undefined;
                      try { if (typeof val?.format === 'function') return val.format('YYYY-MM-DD'); } catch {}
                      try { return new Date(val).toISOString().split('T')[0]; } catch { return undefined; }
                    };
                    const df = formatDateInput(dateRange?.[0]);
                    const dt = formatDateInput(dateRange?.[1]);
                    loadReturnOrders(currentPage, pageSize, searchText, statusFilter, df, dt);
                  }}
                  loading={loading}
                  size="small"
                >
                  {t('刷新')}
                                                  </Button>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  size="small"
                >
                  {t('導出')}
                                                  </Button>
              </Space>
            </Row>
          </Col>
        </Row>
      </Card>

      {/* 退貨申請列表 */}
      <Modal
        title={t('按狀態的總退款金額')}
        open={refundChartVisible}
        footer={null}
        onCancel={() => setRefundChartVisible(false)}
        width={640}
      >
        <div style={{ height: 340 }}>
          <Pie
            data={{
              labels: ['待處理', '處理中', '已完成', '已拒絕', '已批准'],
              datasets: [
                {
                  label: t('退款金額'),
                  data: (() => {
                    const sumBy = (status: string) =>
                      filteredReturns
                        .filter(r => r.status === status)
                        .reduce((acc, cur) => acc + (Number(cur.totalRefundAmount) || 0), 0);
                    return [
                      sumBy('pending'),
                      sumBy('processing'),
                      sumBy('completed'),
                      sumBy('rejected'),
                      sumBy('approved'),
                    ];
                  })(),
                  backgroundColor: [
                    'rgba(250, 173, 20, 0.6)',
                    'rgba(114, 46, 209, 0.6)',
                    'rgba(82, 196, 26, 0.6)',
                    'rgba(245, 34, 45, 0.6)',
                    'rgba(24, 144, 255, 0.6)'
                  ],
                  borderColor: [
                    'rgba(250, 173, 20, 1)',
                    'rgba(114, 46, 209, 1)',
                    'rgba(82, 196, 26, 1)',
                    'rgba(245, 34, 45, 1)',
                    'rgba(24, 144, 255, 1)'
                  ],
                  borderWidth: 1
                }
              ]
            }}
            options={{
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'right' },
                tooltip: { enabled: true }
              }
            }}
          />
        </div>
      </Modal>

      {/* 退貨申請列表 */}
      <Card 
        title={t('退貨申請列表')}
        extra={
          <Space>
            <Button type="primary" onClick={() => setCreateModalVisible(true)}>{t('新建退貨申請')}</Button>
            <Dropdown 
              overlay={columnSettingsMenu} 
              trigger={['click']} 
              placement="bottomRight"
              open={columnSettingsVisible}
              onOpenChange={setColumnSettingsVisible}
            >
              <Button icon={<ColumnHeightOutlined />}>
                {t('列設置')}
                                      </Button>
            </Dropdown>
          </Space>
        }
      >
        <Table
          columns={draggableReturnColumns as any}
          dataSource={sortData(filteredReturns)}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total) => t('共 {{total}} 條記錄', { total }),
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* 退貨處理模態框 */}
      <Modal
        title={t('退貨處理')}
        open={processingModalVisible}
        onCancel={() => setProcessingModalVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}
        destroyOnClose
      >
        {selectedReturn && (
          <ReturnOrderProcessing
            order={(selectedReturn as any).originalOrder}
            onStatusUpdate={async (status, notes, extra) => {
              try {
                await apiService.processReturn(selectedReturn.id, {
                  status,
                  merchantConfirmation: notes,
                  returnReason: extra?.returnReason,
                  handlingMethod: extra?.handlingMethod,
                });
                handleStatusUpdate(selectedReturn.id, status, notes);
              } catch (e) {
                console.error('提交處理結果失敗:', e);
                message.error(t('提交處理結果失敗'));
              }
            }}
            onComplete={handleProcessingComplete}
          />
        )}
      </Modal>
      {/* 退貨詳情模態框（只讀、不可編輯、無下一步） */}
      <Modal
        title={selectedReturn ? `退貨詳情 - ${selectedReturn.returnNumber}` : t('退貨詳情')}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={<Button onClick={() => setDetailModalVisible(false)}>{t('關閉')}</Button>}
        width={900}
        style={{ top: 24 }}
        destroyOnClose
      >
        {selectedReturn && (
          <ReturnOrderProcessing
            mode="detail"
            order={(selectedReturn as any).originalOrder}
            onStatusUpdate={() => {}}
            onComplete={() => {}}
            initialValues={{
              returnReason: selectedReturn.returnReason || '',
              handlingMethod: selectedReturn.handlingMethod || 'refund',
              merchantConfirmation: selectedReturn.handlingNotes || '',
            }}
            overrideReturnItems={(selectedReturn.items && selectedReturn.items.length > 0)
              ? selectedReturn.items.map((it: any) => ({
                  id: it.id,
                  originalOrderItemId: it.originalOrderItemId,
                  productId: it.productId,
                  product: it.product || { id: it.productId, name: it.productName || '-', sku: it.productSku || '-' },
                  originalQuantity: it.originalQuantity,
                  returnQuantity: it.returnQuantity,
                  unitPrice: it.unitPrice,
                  returnReason: it.returnReason,
                  condition: it.condition,
                  restockable: it.restockable,
                  notes: it.notes,
                }))
              : (selectedReturn.originalOrder?.items || []).map((it: any) => ({
                  id: it.id,
                  originalOrderItemId: it.id,
                  productId: it.productId,
                  product: it.product,
                  originalQuantity: it.quantity,
                  returnQuantity: it.returnedQuantity || 0,
                  unitPrice: it.unitPrice,
                  returnReason: '-',
                  condition: '-',
                  restockable: false,
                }))}
          />
        )}
      </Modal>

      {/* 查看詳情模態框 */}
      <Modal
        title={t('退貨詳情')}
        open={detailsVisible}
        onCancel={() => { setDetailsVisible(false); setSelectedReturn(null); }}
        footer={[
          <Button key="close" onClick={() => { setDetailsVisible(false); setSelectedReturn(null); }}>
            {t('關閉')}
                          </Button>
        ]}
        width={800}
        destroyOnClose
      >
        {selectedReturn && (
          <ReturnOrderProcessing
            mode="detail"
            order={{
              orderNumber: selectedReturn.originalOrder?.orderNumber || (selectedReturn as any).originalOrderId || '-',
              customer: { name: selectedReturn.customer?.name || '-' },
              totalAmount: Number(selectedReturn.totalRefundAmount || 0),
              items: [],
            } as any}
            onStatusUpdate={() => {}}
            onComplete={() => {}}
            initialValues={{
              returnReason: (selectedReturn as any).returnReason || (selectedReturn as any).merchantConfirmation || '',
              handlingMethod: selectedReturn.handlingMethod || 'refund',
              merchantConfirmation: selectedReturn.handlingNotes || '',
            }}
            overrideReturnItems={(selectedReturn.items && (selectedReturn.items as any[]).length > 0)
              ? (selectedReturn.items as any[]).map((it: any) => ({
                  id: it.id,
                  originalOrderItemId: it.originalOrderItemId,
                  productId: it.productId,
                  product: it.product || { id: it.productId, name: it.productName || '-', sku: it.productSku || '-' },
                  originalQuantity: Number(it.originalQuantity || 0),
                  returnQuantity: Number(it.returnQuantity || 0),
                  unitPrice: Number(it.unitPrice || 0),
                  returnReason: it.returnReason,
                  condition: it.condition,
                  restockable: Boolean(it.restockable),
                  notes: it.notes,
                }))
              : (((selectedReturn as any).originalOrder?.items || []) as any[]).map((it: any) => ({
                  id: it.id,
                  originalOrderItemId: it.id,
                  productId: it.productId,
                  product: it.product || { id: it.productId, name: it.productName || '-', sku: it.productSku || '-' },
                  originalQuantity: Number(it.quantity || 0),
                  returnQuantity: Number(it.returnedQuantity || 0),
                  unitPrice: Number(it.unitPrice || 0),
                  returnReason: '-',
                  condition: '-',
                  restockable: false,
                }))}
          />
        )}
      </Modal>

      {/* 新建退貨申請 */}
      <Modal
        title={t('新建退貨申請')}
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onOk={async () => {
          try {
            const values = await createForm.validateFields();
            const payload: any = {
              originalOrderId: values.originalOrderId,
              customerId: values.customerId,
              returnDate: values.returnDate ? values.returnDate.toISOString() : undefined,
              returnReason: values.returnReason || undefined,
              totalRefundAmount: values.totalRefundAmount != null ? Number(values.totalRefundAmount) : undefined,
              refundMethod: values.refundMethod || undefined,
              handlingMethod: values.handlingMethod || undefined,
              warehouseId: values.warehouseId || undefined,
              createdBy: values.createdBy || undefined
            };
            if (!payload.originalOrderId || !payload.customerId) {
              message.error(t('請填寫原訂單ID與客戶ID'));
              return;
            }
            setLoading(true);
            // 只提交一次，使用返回值中的最終單號（若有）
            const created: any = await apiService.createReturn(payload);
            if (created?.returnNumber) setPreviewReturnNumber(created.returnNumber);
            message.success(t('已建立退貨申請'));
            setCreateModalVisible(false);
            createForm.resetFields();
            // 重新整理列表並帶上目前的查詢條件與日期範圍
            const formatDateInput = (val: any) => {
              if (!val) return undefined;
              try { if (typeof val?.format === 'function') return val.format('YYYY-MM-DD'); } catch {}
              try { return new Date(val).toISOString().split('T')[0]; } catch { return undefined; }
            };
            const df = formatDateInput(dateRange?.[0]);
            const dt = formatDateInput(dateRange?.[1]);
            setCurrentPage(1);
            await loadReturnOrders(1, pageSize, searchText, statusFilter, df, dt);
          } catch (e) {
            if ((e as any)?.errorFields) return; // 表單校驗失敗
            console.error('建立退貨申請失敗:', e);
            message.error(t('建立退貨申請失敗'));
          } finally {
            setLoading(false);
          }
        }}
        okText={t('提交')}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onValuesChange={(_, allValues) => {
            // 保持預覽號碼不因表單變化而清空
          }}
          onMouseEnter={async () => {
            try {
              if (!previewReturnNumber) {
                const resp: any = await apiService.getNextReturnNumber();
                setPreviewReturnNumber(resp?.nextNumber || '');
              }
            } catch {}
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('退貨申請號')}>
                <Input placeholder={t('提交後自動生成')} value={previewReturnNumber} disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('狀態')}>
                <Input value="待處理 (pending)" disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="originalOrderId" label={t('原訂單號')} rules={[{ required: true, message: t('請輸入原訂單號') }]}>
                <Input placeholder={t('例如：SO-2024-001 或 業務系統訂單ID')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customerId" label={t('客戶')} rules={[{ required: true, message: t('請輸入客戶') }]}>
                <Input placeholder={t('例如：客戶ID 或名稱（可先填ID）')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="returnDate" label={t('申請日期')}>
                <DatePicker style={{ width: '100%' }} placeholder={t('留空則自動取當前時間')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="totalRefundAmount" label={t('退款金額')}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={t('退貨商品')}>
                <Input placeholder={t('提交後在退貨處理中管理明細')} disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="refundMethod" label={t('退款方式')}>
                <Select allowClear placeholder={t('選擇退款方式')}>
                  <Option value="original_payment">{t('原路退回')}</Option>
                  <Option value="bank_transfer">{t('銀行轉賬')}</Option>
                  <Option value="store_credit">{t('商家代金券')}</Option>
                  <Option value="other">{t('其他')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="handlingMethod" label={t('處理方式')}>
                <Select allowClear placeholder={t('選擇處理方式')}>
                  <Option value="refund">{t('退款')}</Option>
                  <Option value="exchange">{t('換貨')}</Option>
                  <Option value="replacement">{t('更換')}</Option>
                  <Option value="partial_refund">{t('部分退款')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="warehouseId" label={t('倉庫')} rules={[{ required: true, message: t('請選擇倉庫') }]}>
                <Select
                  placeholder={t('選擇倉庫')}
                  onFocus={async () => {
                    if (Array.isArray(warehouseOptions) && warehouseOptions.length > 0) return;
                    try {
                      const resp: any = await apiService.getWarehouses({ page: 1, limit: 1000 });
                      const rows: any[] = Array.isArray(resp)
                        ? resp
                        : (Array.isArray(resp?.data) ? resp.data : []);
                      const opts = rows.map((w: any) => ({ id: w.id, name: w.name ?? w.code ?? w.id }));
                      setWarehouseOptions(opts);
                    } catch (err) {
                      console.error('加載倉庫失敗:', err);
                      message.error(t('加載倉庫失敗'));
                    }
                  }}
                  allowClear
                >
                  {(Array.isArray(warehouseOptions) ? warehouseOptions : []).map((w: any) => (
                    <Option key={w.id} value={w.id}>{w.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="createdBy" label={t('創建人')}>
                <Input placeholder={t('當前用戶或員工ID')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="returnReason" label={t('退貨原因')}>
                <Input.TextArea rows={3} placeholder={t('簡述退貨原因')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ReturnManagement;
